import { useState } from 'react'
import { AlertTriangle, ArrowRight, Check, Play, X } from 'lucide-react'
import { Badge, type BadgeTone } from '../../components/StatusBadge'
import { Card } from '../../components/Card'
import type { InboxItem } from '../../api/inbox'
import { KIND_LABELS, SOURCE_LABELS, isActionable } from './groupItems'

// Quelle -> Badge-Ton (nutzt das bestehende Badge-Farbsystem).
const SOURCE_TONE: Record<string, BadgeTone> = {
  harvest: 'accent',
  rule: 'cool',
  portfolio: 'info',
  recommendation: 'info',
  experiment: 'cool',
  ai_task: 'ink',
  manual: 'neutral',
}

// Ablehnungsgruende doppeln als Lernsignal fuer die spaetere Auswertung.
const REJECT_REASONS = ['Bid zu aggressiv', 'Falsches Produkt', 'Timing', 'Sonstiges'] as const

function eur(value: string | number | null): string {
  if (value == null) return '—'
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(n)) return '—'
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
}

export interface InboxCardProps {
  item: InboxItem
  isDemoMode: boolean
  busy?: boolean
  onApprove: () => void
  onReject: (note: string) => Promise<unknown> | void
  onApply: () => void
  onPromoteDraft: () => void
}

export function InboxCard({ item, isDemoMode, busy = false, onApprove, onReject, onApply, onPromoteDraft }: InboxCardProps) {
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState<string>(REJECT_REASONS[0])
  const [detail, setDetail] = useState('')

  // Kind-Label: erst der bekannte expectedEffect.kind, sonst der konkrete eventType
  // (z.B. 'bid_update'), erst als letzter Fallback das generische 'Sonstige'.
  const rawKind = item.expectedEffect?.kind
  const kindLabel = rawKind
    ? (KIND_LABELS[rawKind] ?? rawKind)
    : (item.eventType ?? KIND_LABELS['other'])
  const target = item.campaignName ?? item.keywordText ?? item.searchTerm ?? item.draftTargetName ?? 'Ohne Zuordnung'
  const hasBidChange = item.suggestedBid != null && item.bidAtEventTime != null
  // Im Demo-Modus blockt das Backend approve/reject/apply/batch/promote mit 423
  // (alle hinter requireWriteMode). Also hier jede Mutation deaktivieren statt
  // Buttons anzubieten, die garantiert fehlschlagen.
  const locked = busy || isDemoMode
  // Spine-3-Integration: Fenster I fuehrt 'reconcile_required' ein (Apply-Versuch
  // mit unbekanntem Amazon-Ausgang). Der Operator loest ihn ueber approve
  // ("Amazon geprueft, kein Write -> erneut freigeben") oder reject auf.
  const isReconcile = item.status === 'reconcile_required'
  // Ablehnen akzeptiert das Backend fuer pending UND reconcile_required
  // (OPERATOR_DECIDABLE_STATUSES, Fenster I) — nicht mehr nur pending.
  const canReject = item.status === 'pending' || isReconcile
  // Fail-closed: Amazon-wirksame Aktionen nur bei bekanntem Ziel UND Effekt.
  const actionable = isActionable(item)
  const writeLocked = locked || !actionable
  const writeTitle = (fallback: string): string =>
    isDemoMode
      ? 'Im Demo-Modus deaktiviert (kein Schreibzugriff)'
      : !actionable
        ? 'Kontext unvollständig (Ziel/Effekt unbekannt) — aus Sicherheitsgründen gesperrt'
        : fallback

  const submitReject = () => {
    const note = detail.trim() ? `${reason}: ${detail.trim()}` : reason
    // Editor + Notiz erst nach ERFOLG schliessen — schlaegt der Request fehl
    // (z.B. Notiz > 500 Zeichen, Netz/Server), bleibt der Text erhalten; der
    // Fehler selbst wird ueber den globalen Mutations-Toast gemeldet.
    Promise.resolve(onReject(note))
      .then(() => {
        setRejecting(false)
        setDetail('')
      })
      .catch(() => {
        /* Editor bewusst offen lassen, Notiz nicht verwerfen */
      })
  }

  return (
    <Card className="p-3.5">
      {/* Kopf: Quelle + Ziel links, Money-first-Anker rechts */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone={SOURCE_TONE[item.source] ?? 'neutral'}>{SOURCE_LABELS[item.source] ?? item.source}</Badge>
            <span className="text-[11px] font-medium text-[var(--muted-foreground)]">
              {kindLabel}
            </span>
          </div>
          <p className="mt-1 truncate text-sm font-semibold text-[var(--foreground)]">{target}</p>
          {item.keywordText && item.campaignName && (
            <p className="truncate text-xs text-[var(--muted-foreground)]">{item.keywordText}</p>
          )}
          {item.searchTerm && (
            <p className="truncate text-xs text-[var(--muted-foreground)]">Suchbegriff: {item.searchTerm}</p>
          )}
          {item.targetAdGroupName && (
            <p className="truncate text-xs text-[var(--muted-foreground)]">Anzeigengruppe: {item.targetAdGroupName}</p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] uppercase tracking-wide text-[var(--muted-foreground)]">Spend</p>
          <p className="text-sm font-bold tabular-nums text-[var(--foreground)]">{eur(item.impactSpend)}</p>
        </div>
      </div>

      {/* Body: Begruendung + Bid-Vergleich */}
      {item.reasoning && <p className="mt-2 text-xs text-[var(--muted-foreground)]">{item.reasoning}</p>}
      {hasBidChange && (
        <p className="mt-2 flex items-center gap-1.5 text-xs">
          <span className="tabular-nums text-[var(--muted-foreground)] line-through">{eur(item.bidAtEventTime)}</span>
          <ArrowRight className="h-3 w-3 text-[var(--muted-foreground)]" />
          <span className="tabular-nums font-semibold text-[var(--foreground)]">{eur(item.suggestedBid)}</span>
        </p>
      )}
      {!hasBidChange && item.suggestedBid != null && (
        <p className="mt-2 text-xs">
          <span className="text-[var(--muted-foreground)]">Ziel-Bid: </span>
          <span className="tabular-nums font-semibold text-[var(--foreground)]">{eur(item.suggestedBid)}</span>
        </p>
      )}

      {/* Fehlerzeile: fehlgeschlagener Apply */}
      {item.amazonError && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-red-50 px-2 py-1 text-xs text-red-700">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{isReconcile ? 'Unklarer Amazon-Ausgang' : 'Letzter Apply fehlgeschlagen'}: {item.amazonError}</span>
        </p>
      )}

      {/* Spine-3-Integration: Reconcile-Hinweis mit Handlungsanweisung */}
      {isReconcile && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            Prüfbedürftig: Der Amazon-Ist-Zustand ist unbekannt. Erst bei Amazon prüfen, dann
            „Geprüft — erneut freigeben" (nur wenn nichts geschrieben wurde) oder ablehnen.
          </span>
        </p>
      )}

      {/* Fail-closed-Hinweis: unvollständiger Kontext sperrt Amazon-wirksame Aktionen */}
      {!actionable && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>Ziel oder Effekt unbekannt — Freigeben/Anwenden/Übernehmen aus Sicherheitsgründen gesperrt.</span>
        </p>
      )}

      {/* Aktionen */}
      {rejecting ? (
        <div className="mt-3 space-y-2 rounded-md border border-[var(--border)] p-2">
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Ablehnungsgrund"
            className="w-full rounded-md border border-[var(--border)] bg-[var(--card)] px-2 py-1 text-xs"
          >
            {REJECT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Optionale Notiz (Lernsignal)"
            className="w-full rounded-md border border-[var(--border)] bg-[var(--card)] px-2 py-1 text-xs"
          />
          <div className="flex gap-2">
            <button onClick={submitReject} disabled={locked} className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
              Ablehnen bestätigen
            </button>
            <button onClick={() => setRejecting(false)} className="rounded-md border border-[var(--border)] px-3 py-1 text-xs text-[var(--foreground)]">
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {(item.status === 'pending' || isReconcile) && (
            <button
              onClick={onApprove}
              disabled={writeLocked}
              title={writeTitle(isReconcile ? 'Amazon geprüft — erneut freigeben' : 'Freigeben')}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
            >
              <Check className="h-3 w-3" /> {isReconcile ? 'Geprüft — erneut freigeben' : 'Freigeben'}
            </button>
          )}
          {item.status === 'approved' && (
            <button
              onClick={onApply}
              disabled={writeLocked}
              title={writeTitle('Bei Amazon anwenden')}
              className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
            >
              <Play className="h-3 w-3" /> Anwenden
            </button>
          )}
          {item.status === 'draft' && (
            <button
              onClick={onPromoteDraft}
              disabled={writeLocked}
              title={writeTitle('In die Offen-Queue übernehmen')}
              className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
            >
              <ArrowRight className="h-3 w-3" /> In Queue übernehmen
            </button>
          )}
          {canReject && (
            <button
              onClick={() => setRejecting(true)}
              disabled={locked}
              title={isDemoMode ? 'Im Demo-Modus deaktiviert (kein Schreibzugriff)' : 'Ablehnen'}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1 text-xs font-medium text-[var(--foreground)] disabled:opacity-50"
            >
              <X className="h-3 w-3" /> Ablehnen
            </button>
          )}
        </div>
      )}
    </Card>
  )
}

export default InboxCard
